import styled from 'styled-components';
import React, { useContext } from 'react';
import {
  Box,
  Copy,
  H6,
  Icon,
  Image,
  Optional,
  Pill,
  useAccordion,
  useThemeContext,
} from '@honda-canada/design-system-react';
import { compiler } from 'markdown-to-jsx';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';

import AccordionItem from './AccordionItem';
import HeaderContext from '../service/HeaderContext';
import Link from '../../RoutableSitecoreLink';
import formatDataTestId from '../../../utils/formatDataTestId';
import themeStyles from './Accordion.styles';
import useDealerStatusAndHours from '../service/useDealerStatusAndHours';
import { FIND_DEALER_NAV_ITEM_MOBILE_LEFT_ID } from '../service/constants';
import { stripMarkdownHeading } from '../../../utils/markdown';
import getCtaLink from '../service/utils/getCtaLink';
import useHideDealerByProvinces from '../service/useHideDealerByProvinces';

const TitleH6 = themeStyles.apply(H6, 'TitleH6');
const TitleH7 = themeStyles.apply(Copy, 'TitleH7');
const TitleH8 = themeStyles.apply(Copy, 'TitleH8');
const ExternalIcon = themeStyles.apply(Icon, 'ExternalIcon');
const NavLink = styled(Link)(themeStyles.get('NavLink'));
const ItemImage = themeStyles.apply(Image, 'ItemImage');
const ItemIcon = themeStyles.apply(Image, 'ItemIcon');
const DealerName = themeStyles.apply(Copy, 'DealerName');
const FindDealerLink = themeStyles.apply(Copy, 'FindDealerLink');
const NavItem = themeStyles.apply(Box, 'NavItem');

const MenuMobileAccordionContainer = themeStyles.apply(Box, 'MenuMobileAccordionContainer');

const TITLE_MAP_LINK = 'link';

const TitleMap = {
  1: TitleH6,
  2: TitleH7,
  3: TitleH8,
  [TITLE_MAP_LINK]: NavLink,
  // fallback if level not defined
  default: TitleH8,
};

const TitlePropsMap = {
  1: {},
  2: { size: 'nav' },
  3: { size: 'nav' },
};

const createItem = baseItem => {
  const cta = baseItem.ctaLink || baseItem.url;
  return {
    ...baseItem,
    key: baseItem.itemName,
    label: cta?.text || baseItem.label || baseItem.title || baseItem.categoryTitle,
    cta,
    isItemLink: !!cta?.text && !!cta?.href,
  };
};

const MenuMobileAccordion = ({ items: contentItems, level = 1 }) => {
  const theme = useThemeContext();
  const accordionItems =
    contentItems
      ?.reduce((acc, i) => {
        if (i?.title !== '') {
          acc.push(createItem(i));
        } else {
          const subItems = i?.items?.map(createItem) || [];
          acc.push(...subItems);
        }
        return acc;
      }, [])
      .filter(Boolean) || [];

  const { styles, setIsOpenMenu, setSubMenuList, navHeaderTitleFont, navHeadingFont, footerLastTitleHaveNoBorder } =
    useContext(HeaderContext);
  const behavior = useAccordion(accordionItems);
  const { isActive, toggleItem } = behavior;

  const { dealerName, isDealerDataAvailable } = useDealerStatusAndHours();
  const { sitecoreContext } = useSitecoreContext();
  const productLine = sitecoreContext?.site?.name.toLowerCase();
  const { hideDealer } = useHideDealerByProvinces();

  const isActiveHandler = e => isActive(e);
  const toggleItemHandler = e => toggleItem(e);
  const handlerNavLinkClick = isItemLink => {
    // Close mobile nav menu after clicking links
    if (isItemLink) {
      setSubMenuList({});
      setIsOpenMenu(false);
    }
  };

  return (
    <MenuMobileAccordionContainer styles={styles.drawerBody[level]}>
      {accordionItems?.map((item, index) => {
        const TitleComponent = TitleMap[item.isItemLink ? TITLE_MAP_LINK : level] ?? TitleMap.default;
        const itemKey = item?.key;
        let accordionContent = null;
        let accordionTitleElement = null;
        const isLastItem = footerLastTitleHaveNoBorder && index === accordionItems.length - 1;
        const isFindDealer = item?.itemName?.toLowerCase?.() === FIND_DEALER_NAV_ITEM_MOBILE_LEFT_ID.toLowerCase();
        if (item?.items?.length) {
          accordionContent = <MenuMobileAccordion items={item?.items} level={level + 1} />;
        }

        if (item?.image?.src) {
          accordionTitleElement = (
            <NavLink
              styles={styles.accordion.titleContent[level]}
              data-testid={`navItem-name${formatDataTestId(item?.description)}`}
              paddingY="m"
              field={{
                value: getCtaLink(item),
              }}
            >
              <ItemImage
                src={item?.image?.src}
                alt={item?.image?.alt}
                data-testid={`navItem-logo${formatDataTestId(item?.description)}`}
              />
              <Copy fontFamily={navHeadingFont} data-testid={`navItem-name${formatDataTestId(item?.description)}`}>
                {item?.description}
              </Copy>
            </NavLink>
          );
        }

        if (item?.label) {
          const isExternal = item?.cta?.linktype === 'external';

          accordionTitleElement = (
            <TitleComponent
              isExternal={isExternal}
              isActive={(() => isActiveHandler(itemKey))()}
              styles={styles.accordion.titleContent[level]}
              styling="secondary"
              className="AccordionTitle"
              {...TitlePropsMap[level]}
              field={{
                value: getCtaLink(item),
              }}
              onClick={() => handlerNavLinkClick(item?.isItemLink)}
            >
              <Optional when={!isFindDealer}>
                <Optional when={item?.icon?.src}>
                  <ItemIcon src={item?.icon?.src} alt={item?.icon?.alt} data-testid="cy-navItem-icon" />
                </Optional>
                <NavItem>
                  {compiler(stripMarkdownHeading(item?.label), {
                    namedCodesToUnicode: {
                      copy: '\u00A9',
                    },
                  })}
                </NavItem>
              </Optional>
              <Optional when={isFindDealer}>
                <Optional when={!isDealerDataAvailable || hideDealer}>
                  <ItemIcon src={item?.icon?.src} alt={item?.icon?.alt} data-testid="cy-navItem-icon" />
                  <FindDealerLink fontFamily={styles.navMenuItemFont} size="nav">
                    {item?.label}
                  </FindDealerLink>
                </Optional>
                <Optional when={isDealerDataAvailable && !hideDealer}>
                  <Icon
                    name={`${productLine}MapMarker`}
                    iconSize="default"
                    mr="xs"
                    iconColor={styles.findDealerLinkColor}
                  />
                  <DealerName
                    data-testid="header-dealer-name-subnav"
                    size="small"
                    fontFamily={styles.navMenuItemFont}
                    linkColor={styles.findDealerLinkColor}
                  >
                    {dealerName}
                  </DealerName>
                </Optional>
              </Optional>

              <Optional when={item.pillLabel}>
                <Pill fontFamily={navHeaderTitleFont} pillLabel={item.pillLabel} pillType={item.pillType} ml="xs" />
              </Optional>
              <Optional when={item.isItemLink && isExternal}>
                <ExternalIcon
                  color={styles.accordion.titleContent[level]({ isExternal, theme })?.linkStyle?.color}
                  name="externalLink"
                />
              </Optional>
            </TitleComponent>
          );
        }

        return (
          <AccordionItem
            key={itemKey}
            titleComponent={accordionTitleElement}
            content={accordionContent}
            isActive={(() => isActiveHandler(itemKey))()}
            toggleItem={() => toggleItemHandler(itemKey)}
            level={level}
            itemLabel={item?.label}
            isLastItem={isLastItem}
            hasDealerItem={isFindDealer}
          />
        );
      })}
    </MenuMobileAccordionContainer>
  );
};

export default MenuMobileAccordion;
